import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Wallet, Users, TrendingUp, Landmark, AlertCircle,
  ArrowUpRight, ArrowDownRight, IndianRupee, Bell, X, Edit2
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import AdminLayout from '../../components/layouts/AdminLayout';
import { apiClient } from '../../App';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [recentLoans, setRecentLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReminders, setShowReminders] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [newBalance, setNewBalance] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, remindersRes, transactionsRes, loansRes] = await Promise.all([
        apiClient.get('/dashboard/stats'),
        apiClient.get('/reminders'),
        apiClient.get('/transactions'),
        apiClient.get('/loans')
      ]);
      
      setStats(statsRes.data);
      setReminders(remindersRes.data);
      setRecentTransactions(transactionsRes.data.slice(0, 5));
      setRecentLoans(loansRes.data.slice(0, 5));
      
      // Show reminders modal if there are any
      if (remindersRes.data.length > 0) {
        setShowReminders(true);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleUpdateBalance = async () => {
    try {
      await apiClient.put('/settings/balance', { available_balance: parseFloat(newBalance) });
      toast.success('Available balance updated successfully');
      setShowBalanceModal(false);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to update balance');
    }
  };

  const openBalanceModal = () => {
    setNewBalance(stats?.available_balance?.toString() || '0');
    setShowBalanceModal(true);
  };

  const statCards = [
    { 
      label: 'Total Fund Pool', 
      value: stats?.total_fund_pool || 0, 
      icon: Landmark, 
      color: '#0A2540',
      bgColor: 'bg-[#0A2540]/5',
      editable: false
    },
    { 
      label: 'Available Balance', 
      value: stats?.available_balance || 0, 
      icon: Wallet, 
      color: '#00C853',
      bgColor: 'bg-[#00C853]/10',
      editable: true
    },
    { 
      label: 'Active Loans', 
      value: stats?.total_active_loans || 0, 
      icon: ArrowUpRight, 
      color: '#2962FF',
      bgColor: 'bg-[#2962FF]/10',
      isCount: true,
      editable: false
    },
    { 
      label: 'Total Profit', 
      value: stats?.total_profit || 0, 
      icon: TrendingUp, 
      color: '#FF9800',
      bgColor: 'bg-[#FF9800]/10',
      editable: false
    },
  ];

  const chartData = stats?.monthly_contributions?.map(item => ({
    month: item.month,
    amount: item.amount
  })) || [];

  const loanDistribution = [
    { name: 'Active', value: stats?.total_active_loans || 0 },
    { name: 'Closed', value: stats?.active_members - (stats?.total_active_loans || 0) }
  ];

  const COLORS = ['#2962FF', '#E2E8F0'];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-600 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-600 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-600 border-yellow-200';
      default: return 'bg-blue-100 text-blue-600 border-blue-200';
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="dashboard-card p-6">
              <div className="skeleton h-12 w-12 rounded-xl mb-4"></div>
              <div className="skeleton h-4 w-24 mb-2"></div>
              <div className="skeleton h-8 w-32"></div>
            </div>
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard" subtitle="Overview of your financial operations">
      {/* Reminders Modal */}
      <Dialog open={showReminders} onOpenChange={setShowReminders}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#2962FF]" />
              Pending Reminders
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {reminders.map((reminder, index) => (
              <motion.div
                key={reminder.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-4 rounded-xl border ${getPriorityColor(reminder.priority)}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold">{reminder.title}</h4>
                    <p className="text-sm mt-1 opacity-80">{reminder.message}</p>
                    {reminder.members?.length > 0 && (
                      <p className="text-xs mt-2 opacity-70">
                        Members: {reminder.members.slice(0, 3).join(', ')}
                        {reminder.members.length > 3 && ` +${reminder.members.length - 3} more`}
                      </p>
                    )}
                  </div>
                  <span className="text-xs uppercase font-bold px-2 py-1 rounded-full bg-white/50">
                    {reminder.priority}
                  </span>
                </div>
              </motion.div>
            ))}
            {reminders.length === 0 && (
              <div className="text-center py-8 text-[#425466]">
                <Bell className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No pending reminders</p>
              </div>
            )}
          </div>
          <Button 
            onClick={() => setShowReminders(false)}
            className="w-full bg-[#0A2540] hover:bg-[#163A5F]"
          >
            Got it, Close
          </Button>
        </DialogContent>
      </Dialog>

      {/* Balance Edit Modal */}
      <Dialog open={showBalanceModal} onOpenChange={setShowBalanceModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#00C853]" />
              Update Available Balance
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="balance">Available Balance (₹)</Label>
              <Input
                id="balance"
                type="number"
                value={newBalance}
                onChange={(e) => setNewBalance(e.target.value)}
                placeholder="Enter available balance"
                data-testid="balance-input"
              />
            </div>
            <p className="text-sm text-[#425466]">
              This amount will be visible to all members on their dashboard.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBalanceModal(false)}>Cancel</Button>
            <Button 
              onClick={handleUpdateBalance}
              className="bg-[#00C853] hover:bg-[#00A843]"
              data-testid="save-balance-btn"
            >
              Save Balance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            className="dashboard-card p-6 relative"
          >
            {stat.editable && (
              <button
                onClick={openBalanceModal}
                className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="edit-balance-btn"
              >
                <Edit2 className="w-4 h-4 text-[#425466]" />
              </button>
            )}
            <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center mb-4`}>
              <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
            </div>
            <p className="text-sm text-[#425466] mb-1">{stat.label}</p>
            <p className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {stat.isCount ? stat.value : formatCurrency(stat.value)}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Reminder Banner */}
      {reminders.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 p-4 rounded-xl bg-[#2962FF]/10 border border-[#2962FF]/20 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-[#2962FF]" />
            <span className="text-[#0A2540]">
              You have <strong>{reminders.length}</strong> pending reminder(s) that need attention
            </span>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowReminders(true)}
            className="border-[#2962FF] text-[#2962FF] hover:bg-[#2962FF]/10"
          >
            View All
          </Button>
        </motion.div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Contribution Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="lg:col-span-2 dashboard-card p-6"
        >
          <h3 className="text-lg font-semibold text-[#0A2540] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Monthly Contributions
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00C853" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#00C853" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#425466" fontSize={12} />
                <YAxis stroke="#425466" fontSize={12} tickFormatter={(v) => `₹${v/1000}k`} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'rgba(255,255,255,0.95)', 
                    border: 'none', 
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value) => [formatCurrency(value), 'Amount']}
                />
                <Area 
                  type="monotone" 
                  dataKey="amount" 
                  stroke="#00C853" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorAmount)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Loan Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="dashboard-card p-6"
        >
          <h3 className="text-lg font-semibold text-[#0A2540] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Loan Distribution
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={loanDistribution}
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {loanDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4">
            {loanDistribution.map((item, index) => (
              <div key={item.name} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                <span className="text-sm text-[#425466]">{item.name}: {item.value}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transactions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="dashboard-card overflow-hidden"
        >
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Recent Transactions
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {recentTransactions.map((tx) => (
              <div key={tx.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    tx.type === 'contribution' ? 'bg-green-100' :
                    tx.type === 'loan_disbursement' ? 'bg-blue-100' :
                    tx.type === 'emi_payment' ? 'bg-purple-100' :
                    tx.type === 'penalty' ? 'bg-red-100' : 'bg-orange-100'
                  }`}>
                    {tx.type === 'contribution' ? <ArrowDownRight className="w-5 h-5 text-green-600" /> :
                     tx.type === 'loan_disbursement' ? <ArrowUpRight className="w-5 h-5 text-blue-600" /> :
                     <IndianRupee className="w-5 h-5 text-purple-600" />}
                  </div>
                  <div>
                    <p className="font-medium text-[#0A2540] text-sm">{tx.member_name}</p>
                    <p className="text-xs text-[#425466] capitalize">{tx.type.replace('_', ' ')}</p>
                  </div>
                </div>
                <p className={`font-semibold ${
                  tx.type === 'contribution' || tx.type === 'emi_payment' ? 'text-green-600' : 'text-[#0A2540]'
                }`}>
                  {tx.type === 'contribution' || tx.type === 'emi_payment' ? '+' : '-'}{formatCurrency(tx.amount)}
                </p>
              </div>
            ))}
            {recentTransactions.length === 0 && (
              <div className="p-8 text-center text-[#425466]">No recent transactions</div>
            )}
          </div>
        </motion.div>

        {/* Recent Loans */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.7 }}
          className="dashboard-card overflow-hidden"
        >
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Recent Loans
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {recentLoans.map((loan) => (
              <div key={loan.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#2962FF]/10 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-[#2962FF]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0A2540] text-sm">{loan.member_name}</p>
                    <p className="text-xs text-[#425466]">{loan.tenure_months} months @ {loan.interest_rate}%</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-[#0A2540]">{formatCurrency(loan.amount)}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    loan.status === 'active' ? 'bg-green-100 text-green-600' :
                    loan.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                    loan.status === 'closed' ? 'bg-gray-100 text-gray-600' :
                    'bg-red-100 text-red-600'
                  }`}>
                    {loan.status}
                  </span>
                </div>
              </div>
            ))}
            {recentLoans.length === 0 && (
              <div className="p-8 text-center text-[#425466]">No recent loans</div>
            )}
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
